import React, { useState, useEffect, useContext, createRef } from 'react'
// * Kendo dependencies
import {
    ListBox,
    ListBoxToolbar,
    processListBoxData
} from '@progress/kendo-react-listbox';
// * Helpers
import Notifications, { ERROR_NOTIFICATION, SUCCESS_NOTIFICATION } from '../../helpers/notifications';
import AuthContext from '../../context/Auth/AuthContext';
import { useTranslation } from 'react-i18next';
import API from "../../services/api";
// Constants
import { TRANSFER_TO, TRANSFER_FROM } from './constants';
import { VIEW } from '../../assets/Constants/actionsTypes';

/*List of icons used in this component*/
/**Important: to see the full icons list visit: https://react-icons.github.io/react-icons */
import { ImArrowRight, ImArrowLeft } from 'react-icons/im'

// Used to show the selected item inside the box
const SELECTED_FIELD = "selected";

const Step3 = (props) => {
    const testRef = createRef(null);
    // * we call the hook to make translations
    const { t } = useTranslation('common');
    // * ListBox components state
    const [state, setState] = React.useState({
        benefitsList: [],
        availableGroups: [],
        addedGroups: []
    });
    // * Selected benefit id state
    const [selectedBenefitId, setSelectedBenefitId] = useState(0)
    const [selectedPopGroupId, setSelectedPopGroupId] = useState(0)
    const [selectedBeneficiaryUnit, setSelectedBeneficiaryUnit] = useState('')
    // * Session context to get the current institution data
    const { get_currentInstitution } = useContext(AuthContext);

    // * we use useEffect to call the function that returns the step2 data
    useEffect(() => {
        if (props.activeStep === 3) {
            getStep3_data();
        }
    }, [props.activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

    // *
    const getStep3_data = async () => {
        try {
            // Gets the data from each of the lists, they are all fetched at once 
            // to avoid losing data for different setSate in different functions.
            const responseBenefits = await API.get(`/model/step3ModelBenefits?modelId=${props.modelData.modelid}`);
            // if error in request
            if (responseBenefits.data.code !== 100) {
                Notifications.notify(responseBenefits.data.msg, ERROR_NOTIFICATION);
                return;
            } else {
                // Set the state and select the option
                const benefitsListBD = responseBenefits.data.data
                const benefitsListFirstSelected = benefitsListBD.map((item, i) => {
                    if (i === 0) {
                        item[SELECTED_FIELD] = true;
                    } else {
                        item[SELECTED_FIELD] = false;
                    }
                    return item;
                });
                // saves the benefit id as state
                setSelectedBenefitId(benefitsListBD[0].benefitId);
                getGroupsByBenefit(benefitsListFirstSelected, benefitsListBD[0].benefitId);
            }
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    // * This function is called when the PREVIOUS arrow is clicked.
    // * It is passed as parameter to each step 
    const onPreviousClick = () => {
        props.changeStep(2);
    }

    // * This function is called when the NEXT arrow is clicked.
    // * It is passed as parameter to each step 
    const onNextClick = () => {
        validateNext();
    }

    const validateNext = async () => {
        //https://localhost:44350/api/Model/step3Validation?modelId=1
        try {

            const response = await API.get(`/model/step3Validation?modelId=${props.modelData.modelid}`);

            // if error in request
            if (response.data.code !== 100) {
                Notifications.notify(response.data.msg, ERROR_NOTIFICATION);
            } else {
                if (response.data.data) {
                    props.changeStep(4);
                } else {
                    Notifications.notify(t('model.step03_atleastonegroup_error'), ERROR_NOTIFICATION);
                }
            }
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    // Triggered when an user clicks an element inside the box of Groups or benefit groups
    const handleItemClick = (event, data, connectedData) => {
        setState({
            ...state,
            [data]: state[data].map((item) => {
                if (item.name === event.dataItem.name) {
                    item[SELECTED_FIELD] = !item[SELECTED_FIELD];
                } else {
                    item[SELECTED_FIELD] = false;
                }

                return item;
            }),
            [connectedData]: state[connectedData].map((item) => {
                item[SELECTED_FIELD] = false;
                return item;
            }),
        });
        // Used to know if the selected pop group is HouseHold or Individual. This to make a validation
        setSelectedBeneficiaryUnit(event.dataItem.beneficiaryUnit);
        setSelectedPopGroupId(event.dataItem.popGroupId);
    };

    // Triggered when an user clicks an element inside the box of Benefits
    const modelBenfitsClick = async (event, data) => {
        // Set the state and select the option
        setState({
            ...state,
            [data]: state[data].map((item) => {
                if (item.name === event.dataItem.name) {
                    item[SELECTED_FIELD] = !item[SELECTED_FIELD];
                } else {
                    item[SELECTED_FIELD] = false;
                }
                return item;
            })
        });
        // saves the benefit id as state
        setSelectedBenefitId(event.dataItem.benefitId);
        getGroupsByBenefit(state.benefitsList, event.dataItem.benefitId);
    }

    const getGroupsByBenefit = async (benefitlist, benefitId) => {
        // Gets the available pop groups according to selected benefit
        const responseGroups = await API.get(`/model/step3PopGroups?institutionId=${get_currentInstitution().catInstitutionId}&benefitId=${benefitId}&modelId=${props.modelData.modelid}`);
        // gets the added pop Groups
        const responseAddedGroups = await API.get(`/model/step3AddedPopGroups?modelId=${props.modelData.modelid}&benefitId=${benefitId}`);
        setState({
            ...state,
            benefitsList: benefitlist,
            availableGroups: responseGroups.data.data,
            addedGroups: responseAddedGroups.data.data
        });
    }

    // Click in the arrows of the lists
    const handleToolBarClick = async (e) => {
        let toolName = e.toolName || "";
        let result = processListBoxData(
            state.addedGroups,
            state.availableGroups,
            toolName,
            SELECTED_FIELD
        );

        let responseState = await arrowAction(toolName);

        if (responseState) {
            setState({
                ...state,
                availableGroups: result.listBoxTwoData,
                addedGroups: result.listBoxOneData,
            });
        }
    };

    // if TRANSFER_TO the benefit is saved in the model
    // if TRANSFER_FROM the benefit is removed from the model
    const arrowAction = async (toolName) => {
        let canSave = true;
        try {
            if (toolName === TRANSFER_TO) {
                // calls the API to remove the benefit
                const response = await API.delete(`/model/step3DeletePopGroup?modelId=${props.modelData.modelid}&benefitId=${selectedBenefitId}&popGroupId=${selectedPopGroupId}`);
                // Message
                if (response.data.code !== 100) {
                    Notifications.notify(response.data.msg, ERROR_NOTIFICATION);
                    return false;
                } else {
                    Notifications.notify(t('model.popGroupAddedRemoved'), SUCCESS_NOTIFICATION);
                    return true;
                }
            } else {
                if (state.addedGroups !== []) {
                    state.addedGroups.forEach((element, i) => {
                        // Only the first element matters
                        if (i === 0) {
                            // A benefit can only have Pop Groups of the same beneficiary unit.
                            if (selectedBeneficiaryUnit !== element.beneficiaryUnit) {
                                canSave = false;
                            }
                        }
                    });
                }

                if (!canSave) {
                    Notifications.notify(t('model.wrongPopGroup'), ERROR_NOTIFICATION);
                    return false;
                } else {
                    // * If there are no groups added to the list, no beneficiary unit validation is required.
                    // calls the API to save the pop Group
                    const response = await API.post(`/model/step3InsertPopGroup?modelId=${props.modelData.modelid}&benefitId=${selectedBenefitId}&popGroupId=${selectedPopGroupId}`);
                    // Message
                    if (response.data.code !== 100) {
                        Notifications.notify(response.data.msg, ERROR_NOTIFICATION);
                        return false;
                    } else {
                        Notifications.notify(t('model.popGroupAdded'), SUCCESS_NOTIFICATION);
                        return true;
                    }
                }
            }
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    return (
        <div className="col-md-12">
            <div className="row">
                <h6>{t('model.step3_desc')}</h6>
            </div>
            <div className="row row_attrs container_steps">
                <div className="col-sm-12">
                    <div className="form-row">
                        <div className="form-group col-md-3" style={{ textAlign: "center" }}>
                            <label className="ilo_lbl_general" style={{ fontWeight: "bold" }}>{t('model.modelBenefits_title')}</label>
                            <ListBox
                                style={{
                                    height: 350,
                                    width: "100%",
                                    textAlign: "left"
                                }}
                                data={state.benefitsList}
                                textField="name"
                                selectedField={SELECTED_FIELD}
                                onItemClick={(e) => modelBenfitsClick(e, "benefitsList")}
                            />
                        </div>
                        <div className="form-group col-md-1">

                        </div>
                        <div className="form-group col-md-4" style={{ textAlign: "center" }}>
                            <label className="ilo_lbl_general" style={{ fontWeight: "bold" }}>{t('model.benGroups_title')}</label>
                            <ListBox
                                style={{
                                    height: 350,
                                    width: "100%",
                                    textAlign: "left"
                                }}
                                data={state.addedGroups}
                                textField="name"
                                selectedField={SELECTED_FIELD}
                                onItemClick={(e) => handleItemClick(e, "addedGroups", "availableGroups")}
                                toolbar={() => {
                                    return (
                                        <ListBoxToolbar
                                            tools={props.isEditMode ? [
                                                TRANSFER_FROM,
                                                TRANSFER_TO
                                            ] : null}
                                            data={state.addedGroups}
                                            dataConnected={state.availableGroups}
                                            onToolClick={handleToolBarClick}
                                        />
                                    );
                                }}
                            />
                        </div>
                        <div className="form-group col-md-4" style={{ textAlign: "center" }}>
                            <label className="ilo_lbl_general" style={{ fontWeight: "bold" }}>{t('model.allPopGroups_title')}</label>
                            <ListBox
                                ref={testRef}
                                style={{
                                    height: 350,
                                    width: "100%",
                                    textAlign: "left"
                                }}
                                data={state.availableGroups}
                                textField="name"
                                selectedField={SELECTED_FIELD}
                                onItemClick={(e) => handleItemClick(e, "availableGroups", "addedGroups")}
                            />
                        </div>
                    </div>
                    {props.isEditMode ?
                        <small className="buttons_alert_text"><span className="asterisk_color"> * </span> {t('model.wizard_addRemove_alert')}</small>
                        :
                        <></>
                    }
                </div>
            </div>
            <div className="row arrows_position">
                <div className="col-sm-12">
                    <button className="btn_style" type="button" onClick={() => { onPreviousClick(); }} ><ImArrowLeft /></button>
                    {props.isActionView === VIEW ? null : !props.isEditMode ? (
                        <button
                            onClick={() => { props.editSteps(); }}
                            className="btn btn-primary ilo-btn"
                            style={{marginBottom:"10px",marginRight:"3px"}}
                        >
                            {t('model.step7_edit_btn')}
                        </button>
                    ) : null
                    }
                    <button className="btn_style" type="button" onClick={() => { onNextClick(); }} ><ImArrowRight /></button>
                </div>
            </div>
        </div>
    );
}

export default Step3